import { styled } from "goober";
import type { ReactNode } from "react";
import { DragIndicator } from "../DragIndicator";
import { Resizable } from "../Resizable";
import {
  SidePanelDismiss,
  sidePanelOverlayStyles,
  useSidePanelOverlay,
} from "../SidePanelOverlay";
import { ChatpadToggleButton } from "./ChatpadToggleButton";

const Workspace = styled("div")`
  position: relative;
  display: flex;
  flex: 1;
  min-width: 0;
  min-height: 0;

  &[data-overlay="true"] {
    overflow: hidden;
  }
`;

const OutputPane = styled("section")`
  display: flex;
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  box-sizing: border-box;

  ${sidePanelOverlayStyles}
`;

const PanelToggle = styled("div")`
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 1;
`;

// Zero-width so there's no gutter: the output pane butts against the chat and
// the grip zone overflows into the output pane's own left edge.
const ResizeHandle = styled("div")`
  position: relative;
  width: 0;
  height: 100%;
`;

const ResizeGripZone = styled("div")`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ew-resize;
`;

export function ChatWorkspaceLayout({
  children,
  panel,
  open,
  onOpen,
  onClose,
}: {
  children: ReactNode;
  panel: ReactNode;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
}) {
  const { isOverlay, workspaceRef, panelRef, observeWorkspace } =
    useSidePanelOverlay(open, onClose);

  return (
    <Workspace ref={observeWorkspace} data-overlay={isOverlay}>
      <Resizable
        containerRef={workspaceRef}
        orientation="horizontal"
        minSize={360}
        minOppositeSize={360}
        defaultSize="50%"
        storageKey="chatSidePanelWidth"
        overrideSize={isOverlay || !open ? 0 : undefined}
        Handle={
          open && panel && !isOverlay ? (
            <ResizeHandle>
              <ResizeGripZone>
                <DragIndicator
                  data-drag-indicator
                  data-orientation="horizontal"
                />
              </ResizeGripZone>
            </ResizeHandle>
          ) : null
        }
        first={children}
        second={
          panel ? (
            <OutputPane
              ref={panelRef}
              aria-label="Side panel"
              tabIndex={open && isOverlay ? -1 : undefined}
              aria-hidden={!open || undefined}
              inert={!open}
              data-overlay={isOverlay}
              data-expanded={open}
            >
              {panel}
            </OutputPane>
          ) : null
        }
      />
      {panel && (
        <PanelToggle>
          <ChatpadToggleButton floating isOpen={open} onToggle={onOpen} />
        </PanelToggle>
      )}
      {open && panel && isOverlay && (
        <SidePanelDismiss
          aria-label="Dismiss panel"
          tabIndex={-1}
          data-no-pill=""
          onPress={onClose}
          children={null}
        />
      )}
    </Workspace>
  );
}
